/**
 * Manual J Load Calculations
 * Heating and cooling load calculations for HVAC sizing
 */

import {
  INDOOR_CONDITIONS,
  INTERNAL_GAINS,
  OCCUPANCY,
  QUICK_ESTIMATE_FACTORS,
  QUICK_ESTIMATE_ADJUSTMENTS,
  CLIMATE_ZONES,
} from '../constants/climateZones';

// ============================================
// U-VALUES (Heat Transfer Coefficients)
// ============================================

// Wall U-values by construction and insulation
const WALL_U_VALUES = {
  'wood-2x4': {
    'none': 0.25,
    'R-11': 0.082,
    'R-13': 0.072,
    'R-15': 0.065,
    'R-19': 0.053,
    'R-21': 0.048,
    'R-30': 0.035,
  },
  'wood-2x6': {
    'none': 0.22,
    'R-11': 0.075,
    'R-13': 0.068,
    'R-15': 0.062,
    'R-19': 0.050,
    'R-21': 0.046,
    'R-30': 0.033,
  },
  'brick-veneer': {
    'none': 0.20,
    'R-11': 0.070,
    'R-13': 0.062,
    'R-15': 0.058,
    'R-19': 0.048,
    'R-21': 0.044,
    'R-30': 0.032,
  },
  'concrete-block': {
    'none': 0.35,
    'R-11': 0.085,
    'R-13': 0.075,
    'R-15': 0.068,
    'R-19': 0.055,
    'R-21': 0.050,
    'R-30': 0.038,
  },
  'sip': {
    'none': 0.10,
    'R-11': 0.065,
    'R-13': 0.058,
    'R-15': 0.052,
    'R-19': 0.042,
    'R-21': 0.038,
    'R-30': 0.028,
  },
  'icf': {
    'none': 0.08,
    'R-11': 0.055,
    'R-13': 0.050,
    'R-15': 0.045,
    'R-19': 0.038,
    'R-21': 0.035,
    'R-30': 0.025,
  },
};

// Window U-values by type and frame
const WINDOW_U_VALUES = {
  'single-pane': {
    'wood-vinyl': 1.04,
    'aluminum-break': 1.17,
    'aluminum-no-break': 1.30,
  },
  'double-clear': {
    'wood-vinyl': 0.49,
    'aluminum-break': 0.56,
    'aluminum-no-break': 0.65,
  },
  'double-low-e': {
    'wood-vinyl': 0.32,
    'aluminum-break': 0.38,
    'aluminum-no-break': 0.45,
  },
  'triple-pane': {
    'wood-vinyl': 0.20,
    'aluminum-break': 0.25,
    'aluminum-no-break': 0.30,
  },
};

// Window Solar Heat Gain Coefficient (SHGC)
const WINDOW_SHGC = {
  'single-pane': 0.86,
  'double-clear': 0.76,
  'double-low-e': 0.42,
  'triple-pane': 0.35,
};

// Shading multipliers
const SHADING_FACTORS = {
  'none': 1.0,
  'interior-blinds': 0.65,
  'exterior-awning': 0.35,
  'overhang': 0.50,
  'trees': 0.55,
};

// Solar orientation factors for cooling
const ORIENTATION_FACTORS = {
  'N': 0.35,
  'NE': 0.55,
  'E': 0.85,
  'SE': 0.80,
  'S': 0.65,
  'SW': 0.95,
  'W': 1.00,
  'NW': 0.65,
};

// Ceiling/roof U-values by type
const CEILING_U_VALUES = {
  'below-conditioned': 0.0, // No heat transfer to conditioned space
  'below-unconditioned-attic': 0.035, // R-30 typical
  'below-vented-attic': 0.030, // R-38 typical
  'cathedral': 0.040, // R-25 typical
  'flat-roof': 0.045, // R-22 typical
};

// Floor U-values by type and insulation
const FLOOR_U_VALUES = {
  'slab': 0.75, // BTU/hr-ft of perimeter (different calculation)
  'basement-conditioned': 0.0,
  'basement-unconditioned': 0.05,
  'crawlspace-vented': 0.07,
  'crawlspace-sealed': 0.05,
  'open-air': 0.08,
  'conditioned-space': 0.0,
};

// Floor insulation adjustments
const FLOOR_INSULATION_FACTORS = {
  'none': 1.0,
  'R-11': 0.65,
  'R-19': 0.50,
  'R-30': 0.40,
};

// Door U-values
const DOOR_U_VALUES = {
  'solid-wood': 0.40,
  'solid-window': 0.55,
  'sliding-glass': 0.62,
  'french': 0.52,
};

// ============================================
// CORE CALCULATION FUNCTIONS
// ============================================

/**
 * Calculate wall heat loss/gain
 * Q = U × A × ΔT
 */
function calculateWallLoad(wall, deltaT) {
  const construction = wall.construction || 'wood-2x4';
  const insulation = wall.insulation || 'R-13';
  const height = wall.height || 8; // default 8ft ceiling
  const length = wall.length || 0;

  const area = length * height; // sq ft
  const uValue = WALL_U_VALUES[construction]?.[insulation] || 0.072;

  return Math.round(uValue * area * deltaT);
}

/**
 * Calculate window heat loss (heating)
 * Q = U × A × ΔT
 */
function calculateWindowHeatingLoad(window, deltaT) {
  const type = window.type || 'double-low-e';
  const frame = window.frame || 'wood-vinyl';
  const width = (window.width || 36) / 12; // convert to feet
  const height = (window.height || 48) / 12;

  const area = width * height;
  const uValue = WINDOW_U_VALUES[type]?.[frame] || 0.32;

  return Math.round(uValue * area * deltaT);
}

/**
 * Calculate window solar heat gain (cooling)
 * Q = SHGC × A × Solar Intensity × Shading × Orientation
 */
function calculateWindowCoolingLoad(window, deltaT) {
  const type = window.type || 'double-low-e';
  const frame = window.frame || 'wood-vinyl';
  const facing = window.facing || 'S';
  const shading = window.shading || 'none';
  const width = (window.width || 36) / 12;
  const height = (window.height || 48) / 12;

  const area = width * height;
  const uValue = WINDOW_U_VALUES[type]?.[frame] || 0.32;
  const shgc = WINDOW_SHGC[type] || 0.42;
  const shadingFactor = SHADING_FACTORS[shading] || 1.0;
  const orientationFactor = ORIENTATION_FACTORS[facing] || 0.65;

  // Solar radiation (average summer peak BTU/hr/sq ft)
  const solarIntensity = 200;

  // Conduction load
  const conductionLoad = uValue * area * deltaT;

  // Solar gain load
  const solarLoad = shgc * area * solarIntensity * shadingFactor * orientationFactor;

  return Math.round(conductionLoad + solarLoad);
}

/**
 * Calculate ceiling heat loss/gain
 */
function calculateCeilingLoad(floorArea, ceilingType, deltaT) {
  const uValue = CEILING_U_VALUES[ceilingType] || 0.030;
  return Math.round(uValue * floorArea * deltaT);
}

/**
 * Calculate floor heat loss/gain
 */
function calculateFloorLoad(floorArea, perimeter, floorType, floorInsulation, deltaT) {
  if (floorType === 'slab') {
    // Slab edge loss: BTU/hr = F × P × ΔT (where F is factor, P is perimeter)
    const edgeFactor = 0.75; // typical uninsulated slab
    return Math.round(edgeFactor * (perimeter || Math.sqrt(floorArea) * 4) * deltaT);
  }

  const uValue = FLOOR_U_VALUES[floorType] || 0.05;
  const insulationFactor = FLOOR_INSULATION_FACTORS[floorInsulation] || 1.0;

  return Math.round(uValue * insulationFactor * floorArea * deltaT);
}

/**
 * Calculate door heat loss/gain
 */
function calculateDoorLoad(door, deltaT) {
  const type = door.type || 'solid-wood';
  const width = (door.width || 36) / 12;
  const height = (door.height || 80) / 12;

  const area = width * height;
  const uValue = DOOR_U_VALUES[type] || 0.40;

  return Math.round(uValue * area * deltaT);
}

/**
 * Calculate infiltration load
 * Q = 0.018 × CFM × ΔT (sensible)
 * Q = 4840 × CFM × ΔW (latent, for cooling)
 */
function calculateInfiltrationLoad(volume, ach, deltaT, isHeating = true) {
  const cfm = (volume * ach) / 60;
  const sensibleLoad = Math.round(1.08 * cfm * deltaT);

  if (isHeating) {
    return { sensible: sensibleLoad, latent: 0 };
  }

  // For cooling, add latent load (assume 0.01 lb water/lb air diff)
  const latentLoad = Math.round(0.68 * cfm * 10); // simplified latent
  return { sensible: sensibleLoad, latent: latentLoad };
}

/**
 * Calculate internal gains (appliances, lights, people)
 */
function calculateInternalGains(roomType, occupants = 0) {
  const baseGains = INTERNAL_GAINS[roomType] || INTERNAL_GAINS['other'];

  // Add occupant loads
  const occupantSensible = occupants * OCCUPANCY.sensiblePerPerson;
  const occupantLatent = occupants * OCCUPANCY.latentPerPerson;

  return {
    sensible: baseGains.sensible + occupantSensible,
    latent: baseGains.latent + occupantLatent,
  };
}

// ============================================
// MAIN CALCULATION FUNCTIONS
// ============================================

/**
 * Calculate heating load for a room
 * Returns BTU/hr
 */
export function calculateRoomHeatingLoad(roomData, designConditions) {
  if (!roomData || roomData.conditioned === 'no') {
    return { total: 0, breakdown: {} };
  }

  const outdoorTemp = designConditions?.heatingOutdoor ?? 15;
  const indoorTemp = designConditions?.indoorHeating ?? INDOOR_CONDITIONS.heating;
  const deltaT = indoorTemp - outdoorTemp;
  const infiltrationACH = designConditions?.infiltrationACH ?? 0.5;

  const floorArea = roomData.floorArea || 0;
  const ceilingHeight = roomData.ceilingHeight || 8;
  const volume = floorArea * ceilingHeight;

  // Calculate perimeter from floor area (approximate)
  const perimeter = Math.sqrt(floorArea) * 4;

  let wallLoad = 0;
  let windowLoad = 0;
  let doorLoad = 0;

  // Exterior walls
  (roomData.exteriorWalls || []).forEach(wall => {
    wallLoad += calculateWallLoad({ ...wall, height: ceilingHeight }, deltaT);
  });

  // Windows
  (roomData.windows || []).forEach(window => {
    windowLoad += calculateWindowHeatingLoad(window, deltaT);
  });

  // Doors
  (roomData.doors || []).forEach(door => {
    doorLoad += calculateDoorLoad(door, deltaT);
  });

  // Ceiling
  const ceilingLoad = calculateCeilingLoad(floorArea, roomData.ceilingType, deltaT);

  // Floor
  const floorLoad = calculateFloorLoad(
    floorArea, perimeter, roomData.floorType, roomData.floorInsulation, deltaT
  );

  // Infiltration
  const infiltration = calculateInfiltrationLoad(volume, infiltrationACH, deltaT, true);

  const total = wallLoad + windowLoad + doorLoad + ceilingLoad + floorLoad + infiltration.sensible;

  return {
    total: Math.round(total),
    breakdown: {
      walls: wallLoad,
      windows: windowLoad,
      doors: doorLoad,
      ceiling: ceilingLoad,
      floor: floorLoad,
      infiltration: infiltration.sensible,
    },
  };
}

/**
 * Calculate cooling load for a room
 * Returns BTU/hr (sensible + latent)
 */
export function calculateRoomCoolingLoad(roomData, designConditions) {
  if (!roomData || roomData.conditioned === 'no') {
    return { total: 0, sensible: 0, latent: 0, breakdown: {} };
  }

  const outdoorTemp = designConditions?.coolingOutdoor ?? 93;
  const indoorTemp = designConditions?.indoorCooling ?? INDOOR_CONDITIONS.cooling;
  const deltaT = outdoorTemp - indoorTemp;
  const infiltrationACH = designConditions?.infiltrationACH ?? 0.5;
  const occupants = designConditions?.occupants ?? 0;
  const dailyRangeFactor = designConditions?.dailyRangeFactor ?? 0.85;

  const floorArea = roomData.floorArea || 0;
  const ceilingHeight = roomData.ceilingHeight || 8;
  const volume = floorArea * ceilingHeight;
  const perimeter = Math.sqrt(floorArea) * 4;

  let wallLoad = 0;
  let windowLoad = 0;
  let doorLoad = 0;

  // Exterior walls (apply daily range correction)
  (roomData.exteriorWalls || []).forEach(wall => {
    wallLoad += calculateWallLoad({ ...wall, height: ceilingHeight }, deltaT) * dailyRangeFactor;
  });

  // Windows (includes solar gain)
  (roomData.windows || []).forEach(window => {
    windowLoad += calculateWindowCoolingLoad(window, deltaT);
  });

  // Doors
  (roomData.doors || []).forEach(door => {
    doorLoad += calculateDoorLoad(door, deltaT);
  });

  // Ceiling
  const ceilingLoad = calculateCeilingLoad(floorArea, roomData.ceilingType, deltaT);

  // Floor (usually minimal for cooling)
  const floorLoad = calculateFloorLoad(
    floorArea, perimeter, roomData.floorType, roomData.floorInsulation, deltaT * 0.3
  );

  // Infiltration
  const infiltration = calculateInfiltrationLoad(volume, infiltrationACH, deltaT, false);

  // Internal gains
  const internalGains = calculateInternalGains(roomData.name, occupants);

  const sensibleTotal = wallLoad + windowLoad + doorLoad + ceilingLoad + floorLoad +
                        infiltration.sensible + internalGains.sensible;
  const latentTotal = infiltration.latent + internalGains.latent;

  return {
    total: Math.round(sensibleTotal + latentTotal),
    sensible: Math.round(sensibleTotal),
    latent: Math.round(latentTotal),
    breakdown: {
      walls: Math.round(wallLoad),
      windows: Math.round(windowLoad),
      doors: Math.round(doorLoad),
      ceiling: Math.round(ceilingLoad),
      floor: Math.round(floorLoad),
      infiltration: Math.round(infiltration.sensible),
      infiltrationLatent: Math.round(infiltration.latent),
      internal: Math.round(internalGains.sensible),
      internalLatent: Math.round(internalGains.latent),
    },
  };
}

// ============================================
// QUICK ESTIMATE FUNCTIONS
// ============================================

/**
 * Quick estimate for heating load (rule of thumb)
 */
export function quickEstimateHeating(floorArea, climateZone, options = {}) {
  const baseFactor = QUICK_ESTIMATE_FACTORS.heating[climateZone] || 35;

  let factor = baseFactor;

  // Ceiling height adjustment
  const ceilingHeight = options.ceilingHeight || 8;
  if (ceilingHeight > 8) {
    factor *= 1 + (ceilingHeight - 8) * QUICK_ESTIMATE_ADJUSTMENTS.ceilingHeight.perFoot;
  }

  // Insulation adjustment
  const insulation = options.insulation || 'average';
  factor *= QUICK_ESTIMATE_ADJUSTMENTS.insulation[insulation] || 1.0;

  return Math.round(floorArea * factor);
}

/**
 * Quick estimate for cooling load (rule of thumb)
 */
export function quickEstimateCooling(floorArea, climateZone, options = {}) {
  const baseFactor = QUICK_ESTIMATE_FACTORS.cooling[climateZone] || 25;

  let factor = baseFactor;

  // Ceiling height adjustment
  const ceilingHeight = options.ceilingHeight || 8;
  if (ceilingHeight > 8) {
    factor *= 1 + (ceilingHeight - 8) * QUICK_ESTIMATE_ADJUSTMENTS.ceilingHeight.perFoot;
  }

  // Large windows penalty
  if (options.largeWindows) {
    factor *= 1 + QUICK_ESTIMATE_ADJUSTMENTS.windows.largeWindowPenalty;
  }

  // Sun exposure
  const sunExposure = options.sunExposure || 'partial';
  factor *= QUICK_ESTIMATE_ADJUSTMENTS.sunExposure[sunExposure] || 1.0;

  // Insulation adjustment
  const insulation = options.insulation || 'average';
  factor *= QUICK_ESTIMATE_ADJUSTMENTS.insulation[insulation] || 1.0;

  return Math.round(floorArea * factor);
}

// ============================================
// WHOLE HOUSE CALCULATIONS
// ============================================

/**
 * Calculate total house heating load
 */
export function calculateTotalHeatingLoad(roomsData, designConditions) {
  let total = 0;
  const roomLoads = [];

  // Calculate total floor area for minimum load check
  let totalFloorArea = 0;

  (roomsData || []).forEach(roomData => {
    const load = calculateRoomHeatingLoad(roomData, designConditions);
    total += load.total;
    totalFloorArea += roomData.floorArea || 0;
    roomLoads.push({
      roomId: roomData.roomId,
      roomName: roomData.name,
      load: load.total,
      breakdown: load.breakdown,
    });
  });

  // Apply duct loss factor
  const ductFactor = designConditions?.ductHeatingFactor ?? 1.0;
  let adjustedTotal = Math.round(total * ductFactor);

  // MINIMUM FLOOR-BASED LOAD CHECK
  // Industry rule of thumb varies by climate, use 75% of quick estimate as minimum
  const climateZone = designConditions?.climateZone || '4A';
  const minBtuPerSqFt = QUICK_ESTIMATE_FACTORS.heating[climateZone] * 0.75 || 25;
  const minimumLoad = Math.round(totalFloorArea * minBtuPerSqFt);

  // If calculated load is significantly below minimum, use minimum as floor
  if (adjustedTotal < minimumLoad && totalFloorArea > 0) {
    adjustedTotal = minimumLoad;
  }

  return {
    total: adjustedTotal,
    rooms: roomLoads,
    ductLoss: adjustedTotal - total,
  };
}

/**
 * Calculate total house cooling load
 */
export function calculateTotalCoolingLoad(roomsData, designConditions) {
  let sensibleTotal = 0;
  let latentTotal = 0;
  const roomLoads = [];

  // Calculate total floor area for minimum load check
  let totalFloorArea = 0;

  (roomsData || []).forEach(roomData => {
    const load = calculateRoomCoolingLoad(roomData, designConditions);
    sensibleTotal += load.sensible;
    latentTotal += load.latent;
    totalFloorArea += roomData.floorArea || 0;
    roomLoads.push({
      roomId: roomData.roomId,
      roomName: roomData.name,
      total: load.total,
      sensible: load.sensible,
      latent: load.latent,
      breakdown: load.breakdown,
    });
  });

  // Apply duct loss factor
  const ductFactor = designConditions?.ductCoolingFactor ?? 1.0;
  let adjustedSensible = Math.round(sensibleTotal * ductFactor);
  let adjustedLatent = Math.round(latentTotal * ductFactor);

  // MINIMUM FLOOR-BASED LOAD CHECK
  // Industry rule of thumb: ~18-22 BTU/sq ft for cooling (1 ton per 550-600 sq ft)
  // Use 18 BTU/sq ft as minimum to ensure calculations aren't unreasonably low
  const climateZone = designConditions?.climateZone || '4A';
  const minBtuPerSqFt = QUICK_ESTIMATE_FACTORS.cooling[climateZone] * 0.75 || 18;
  const minimumSensibleLoad = Math.round(totalFloorArea * minBtuPerSqFt);

  // If calculated sensible is significantly below minimum, use minimum as floor
  if (adjustedSensible < minimumSensibleLoad && totalFloorArea > 0) {
    adjustedSensible = minimumSensibleLoad;
    // Also adjust latent proportionally (typically 25-30% of sensible in humid climates)
    const latentRatio = adjustedLatent > 0 ? adjustedLatent / (sensibleTotal || 1) : 0.25;
    adjustedLatent = Math.round(adjustedSensible * latentRatio);
  }

  return {
    total: adjustedSensible + adjustedLatent,
    sensible: adjustedSensible,
    latent: adjustedLatent,
    rooms: roomLoads,
    ductLoss: (adjustedSensible + adjustedLatent) - (sensibleTotal + latentTotal),
  };
}

/**
 * Convert BTU/hr to tons (for AC sizing)
 */
export function btuToTons(btu) {
  return Math.round(btu / 12000 * 10) / 10;
}

/**
 * Get recommended equipment size
 */
export function getRecommendedSize(btu, type = 'cooling') {
  const tons = btu / 12000;

  // Round to nearest standard size (including mini-split sizes)
  const standardSizes = [0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5];

  // Allow 10-15% oversizing for cooling, exact match for heating
  const sizeFactor = type === 'cooling' ? 1.1 : 1.0;
  const targetTons = tons * sizeFactor;

  // Find closest standard size
  let recommended = standardSizes[standardSizes.length - 1];
  for (const size of standardSizes) {
    if (size >= targetTons) {
      recommended = size;
      break;
    }
  }

  return {
    tons: recommended,
    actualTons: Math.round(tons * 100) / 100, // Actual calculated tonnage
    btu: recommended * 12000,
    actualBtu: btu,
  };
}

/**
 * Format BTU load for display
 */
export function formatLoad(btu) {
  if (btu >= 1000) {
    return `${(btu / 1000).toFixed(1)}k`;
  }
  return `${btu}`;
}
